import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@starling/core/api/errors';
import { FormError } from '@/components/form/primitives';
import { formatDate, formatMoney } from '@/lib/format';
import {
  findAdWeekPrice,
  useMyAdsQuery,
  useVisionPricingQuery,
  useWithdrawAdMutation,
  type AdMine,
  type AdStatus,
} from './hooks';
import { NEW_AD_PATH } from './AdsFeed';
import listStyles from '@/post/MyListingsPage.module.css';
import styles from './MyAdsPage.module.css';

// «Моя реклама» (V1.4, бриф §3) — заявки по всем моим организациям
// (`GET /vision/my/ads`), свежие сверху. Карточка — вёрстка «Моих объявлений»
// (миниатюра + заголовок + организация + статус-бейдж, цвета по образцу
// статусов объявлений), сумма, период, показы/клики. Действия:
//   * pending | approved — «Отозвать» (инлайн-подтверждение, как снятие
//     объявления); 409 `ad_not_withdrawable` — статус успел смениться,
//     список перечитываем;
//   * approved — блок «Ожидает оплаты: N ₸» + реквизиты (`payment_note`
//     публичного тарифа, если заданы);
//   * rejected — причина модератора;
//   * paused — админ приостановил показ (обратимо, срок не сгорает): бейдж
//     «Приостановлена» + строка «На паузе с …»; считается текущей заявкой.
// Фильтр-пилюли (замечание пользователя 2026-09-11: отклонённые/отозванные
// копятся) — как в «Моих объявлениях»: `?status=current|archive`, дефолт —
// все; фильтрация клиентская (GET /my/ads без параметра статуса).
// Кнопка «Разместить рекламу» — второй вход в форму помимо главной.

type Filter = 'all' | 'current' | 'archive';
const FILTERS: readonly Filter[] = ['all', 'current', 'archive'];
const CURRENT_STATUSES: readonly AdStatus[] = ['pending', 'approved', 'active', 'paused'];

function matchesFilter(ad: AdMine, filter: Filter): boolean {
  if (filter === 'all') return true;
  const current = CURRENT_STATUSES.includes(ad.status);
  return filter === 'current' ? current : !current;
}

/** Классы статус-бейджа — exhaustive по AdStatus. */
const STATUS_CLASS: Record<AdStatus, string> = {
  pending: styles.statusPending ?? '',
  approved: styles.statusApproved ?? '',
  active: listStyles.statusActive ?? '',
  paused: styles.statusPaused ?? '',
  expired: listStyles.statusArchived ?? '',
  stopped: listStyles.statusArchived ?? '',
  rejected: listStyles.statusRemoved ?? '',
  withdrawn: listStyles.statusArchived ?? '',
};

function canWithdraw(ad: AdMine): boolean {
  return ad.status === 'pending' || ad.status === 'approved';
}

function MyAdCard({ ad, paymentNote }: { ad: AdMine; paymentNote: string | null }): JSX.Element {
  const { t, i18n } = useTranslation();
  const withdrawMutation = useWithdrawAdMutation();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<'withdraw' | 'conflict' | null>(null);
  const muted = ad.status === 'expired' || ad.status === 'stopped' || ad.status === 'withdrawn';

  const withdraw = (): void => {
    setError(null);
    withdrawMutation.mutate(ad.id, {
      onSuccess: () => setConfirming(false),
      onError: (err) => {
        setConfirming(false);
        setError(err instanceof ApiError && err.status === 409 ? 'conflict' : 'withdraw');
      },
    });
  };

  return (
    <article className={muted ? `${listStyles.card} ${listStyles.cardRemoved}` : listStyles.card}>
      <div className={styles.row}>
        <img
          className={styles.thumb}
          src={ad.image_url}
          alt=""
          width={120}
          height={50}
          loading="lazy"
        />
        <div className={styles.body}>
          <div className={listStyles.cardTop}>
            <h3 className={listStyles.cardTitle}>{ad.title}</h3>
            <span className={`${listStyles.status} ${STATUS_CLASS[ad.status]}`}>
              {t(`ads.status.${ad.status}`)}
            </span>
          </div>
          <div className={listStyles.cardMeta}>{ad.organization_name}</div>
        </div>
      </div>

      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt>{t('ads.my.price')}</dt>
          <dd>
            {formatMoney(ad.price_kzt, i18n.language)} ₸ ·{' '}
            {t('ads.new.form.weeksOption', { count: ad.weeks })}
          </dd>
        </div>
        {ad.starts_at && ad.ends_at && (
          <div className={styles.fact}>
            <dt>{t('ads.my.period')}</dt>
            <dd>
              {formatDate(ad.starts_at, i18n.language)} – {formatDate(ad.ends_at, i18n.language)}
            </dd>
          </div>
        )}
        {ad.status === 'paused' && ad.paused_at && (
          <div className={styles.fact}>
            <dt>{t('ads.my.pausedSince')}</dt>
            <dd>{formatDate(ad.paused_at, i18n.language)}</dd>
          </div>
        )}
        {(ad.status === 'active' ||
          ad.status === 'paused' ||
          ad.status === 'expired' ||
          ad.status === 'stopped') && (
          <div className={styles.fact}>
            <dt>{t('ads.my.stats')}</dt>
            <dd>{t('ads.my.statsValue', { impressions: ad.impressions, clicks: ad.clicks })}</dd>
          </div>
        )}
      </dl>

      {ad.status === 'approved' && (
        <div className={styles.payment} role="status">
          <div className={styles.paymentTitle}>
            {t('ads.my.awaitingPayment', { amount: formatMoney(ad.price_kzt, i18n.language) })}
          </div>
          {paymentNote && <p className={styles.paymentNote}>{paymentNote}</p>}
        </div>
      )}

      {ad.status === 'rejected' && ad.reject_reason && (
        <p className={listStyles.removedNote}>
          {t('ads.my.rejectReason')}: {ad.reject_reason}
        </p>
      )}

      {error !== null && (
        <FormError>
          {error === 'withdraw' ? t('ads.my.withdrawError') : t('ads.my.withdrawConflict')}
        </FormError>
      )}

      {canWithdraw(ad) && (
        <div className={listStyles.actions}>
          {confirming ? (
            <span className={listStyles.confirm}>
              {t('ads.my.withdrawConfirm')}
              <button
                type="button"
                className={listStyles.confirmYes}
                disabled={withdrawMutation.isPending}
                onClick={withdraw}
              >
                {t('ads.my.withdrawYes')}
              </button>
              <button
                type="button"
                className={listStyles.confirmNo}
                disabled={withdrawMutation.isPending}
                onClick={() => setConfirming(false)}
              >
                {t('ads.my.withdrawCancel')}
              </button>
            </span>
          ) : (
            <button
              type="button"
              className={listStyles.actionButton}
              onClick={() => setConfirming(true)}
            >
              {t('ads.my.withdraw')}
            </button>
          )}
        </div>
      )}
    </article>
  );
}

export function MyAdsPage(): JSX.Element {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMyAdsQuery();
  const pricing = useVisionPricingQuery();
  const paymentNote = findAdWeekPrice(pricing.data)?.payment_note ?? null;
  const raw = searchParams.get('status');
  const filter: Filter = raw === 'current' || raw === 'archive' ? raw : 'all';
  const all = query.data?.items ?? [];
  const items = all.filter((ad) => matchesFilter(ad, filter));

  function selectFilter(next: Filter): void {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'all') params.delete('status');
        else params.set('status', next);
        return params;
      },
      { replace: true },
    );
  }

  return (
    <div className={listStyles.wrap}>
      <div className={styles.header}>
        <h1 className={listStyles.pageTitle}>{t('ads.my.title')}</h1>
        <Link to={NEW_AD_PATH} className={styles.newLink}>
          {t('ads.feed.place')}
        </Link>
      </div>

      <div className={listStyles.filters} role="group" aria-label={t('ads.my.filter.label')}>
        {FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={key === filter}
            className={`${listStyles.filterPill} ${key === filter ? listStyles.filterPillActive : ''}`}
            onClick={() => selectFilter(key)}
          >
            {t(`ads.my.filter.${key}`)}
          </button>
        ))}
      </div>

      {query.isPending && <div className={listStyles.state}>{t('ads.my.loading')}</div>}

      {query.isError && (
        <div className={`${listStyles.state} ${listStyles.stateError}`}>
          {t('ads.my.error')}
          <div>
            <button type="button" className={listStyles.retry} onClick={() => void query.refetch()}>
              {t('ads.my.retry')}
            </button>
          </div>
        </div>
      )}

      {query.isSuccess && items.length === 0 && (
        <div className={listStyles.state}>
          <p className={listStyles.emptyText}>
            {all.length === 0 ? t('ads.my.empty') : t('ads.my.emptyFiltered')}
          </p>
          {all.length === 0 && (
            <Link to={NEW_AD_PATH} className={listStyles.primaryLink}>
              {t('ads.feed.place')}
            </Link>
          )}
        </div>
      )}

      {items.length > 0 && (
        <div className={listStyles.list}>
          {items.map((ad) => (
            <MyAdCard key={ad.id} ad={ad} paymentNote={paymentNote} />
          ))}
        </div>
      )}
    </div>
  );
}
